import React, { useCallback, useEffect, useMemo, useState } from "react";
import { addMonths, format, startOfYear, subMonths } from "date-fns";
import {
  getRevenueSharingReport,
  RevenueSharingReportResponse,
  CashbackTriple,
} from "../api/revenueSharing";
import {
  buildReportTable,
  Granularity,
  PeriodColumn,
  ReportSection,
  ZERO_TRIPLE,
} from "../helpers/revenueSharing";
import { Loader } from "../components/Loader";
import { Button } from "../components/Button";
import { formatCurrency } from "../helpers/formatNumber";
import { ReactComponent as DownloadIcon } from "../assets/icons/download.svg";
import { ReactComponent as ChevronIcon } from "../assets/icons/arrow-down.svg";

const METRICS: { key: keyof CashbackTriple; label: string }[] = [
  { key: "totalCashback", label: "Total cashback" },
  { key: "userCashback", label: "User cashback" },
  { key: "revenue", label: "Revenue" },
];

type QuickRange = "ytd" | "last12Months" | "last24Months";

const generateMonthOptions = () => {
  const options = [];
  let date = new Date(2024, 10, 1); // Nov 2024, before which there is no data
  const currentDate = new Date();

  while (date <= currentDate) {
    options.push({ value: format(date, "yyyy-MM"), label: format(date, "MMM yyyy") });
    date = addMonths(date, 1);
  }

  return options.reverse();
};

const csvEscape = (value: string): string => {
  if (/[",\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
};

const AdminRevenueSharing: React.FC = () => {
  const now = new Date();
  const [fromMonth, setFromMonth] = useState(format(startOfYear(now), "yyyy-MM"));
  const [toMonth, setToMonth] = useState(format(now, "yyyy-MM"));
  const [granularity, setGranularity] = useState<Granularity>("monthly");
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const [report, setReport] = useState<RevenueSharingReportResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [error, setError] = useState(false);
  const [backendUnavailable, setBackendUnavailable] = useState(false);

  const monthOptions = useMemo(generateMonthOptions, []);

  const fetchReport = useCallback(async () => {
    try {
      setLoading(true);
      setError(false);
      const response = await getRevenueSharingReport(fromMonth, toMonth);
      setReport(response);
      setBackendUnavailable(false);
    } catch (err: any) {
      setReport(null);
      if (err?.response?.status === 404) {
        setBackendUnavailable(true);
      } else {
        setError(true);
      }
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  }, [fromMonth, toMonth]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const table = useMemo(
    () => (report ? buildReportTable(report, granularity) : null),
    [report, granularity]
  );

  const hasPartialQuarter =
    granularity === "quarterly" &&
    (table?.periods.some((p) => p.monthCount < 3) ?? false);

  const applyQuickRange = (range: QuickRange) => {
    const current = new Date();
    if (range === "ytd") {
      setFromMonth(format(startOfYear(current), "yyyy-MM"));
    } else {
      setFromMonth(format(subMonths(current, range === "last12Months" ? 11 : 23), "yyyy-MM"));
    }
    setToMonth(format(current, "yyyy-MM"));
  };

  const toggleSection = (id: string) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const setAllExpanded = (value: boolean) => {
    if (!table) return;
    const next: Record<string, boolean> = {};
    for (const section of table.sections) {
      if (section.rows.length > 0) next[section.id] = value;
    }
    setExpanded(next);
  };

  const periodHeader = (period: PeriodColumn) =>
    granularity === "quarterly" && period.monthCount < 3
      ? `${period.label}*`
      : period.label;

  const exportToCsv = () => {
    if (!table) return;

    const header = ["Section", "Partner", "Metric", ...table.periods.map((p) => periodHeader(p))];
    const lines: string[] = [header.join(",")];

    const pushTripleRows = (
      section: string,
      partner: string,
      cells: Record<string, CashbackTriple>
    ) => {
      for (const metric of METRICS) {
        lines.push(
          [
            csvEscape(section),
            csvEscape(partner),
            metric.label,
            ...table.periods.map((p) =>
              (((cells[p.key] ?? ZERO_TRIPLE)[metric.key] as number) / 100).toFixed(2)
            ),
          ].join(",")
        );
      }
    };

    for (const section of table.sections) {
      pushTripleRows(section.label, section.rows.length > 0 ? "All" : "", section.subtotal);
      for (const row of section.rows) {
        pushTripleRows(section.label, row.name, row.cells);
      }
    }
    pushTripleRows("Grand total", "", table.grandTotal);

    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `revenue-sharing-${fromMonth}-${toMonth}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (initialLoad) {
    return (
      <div className="loading-page">
        <Loader />
      </div>
    );
  }

  const renderTripleRows = (
    firstCell: React.ReactNode,
    cells: Record<string, CashbackTriple>,
    options: { rowClass?: string; firstCellClass?: string } = {}
  ) => {
    if (!table) return null;
    const { rowClass = "", firstCellClass = "" } = options;
    return METRICS.map((metric, index) => (
      <tr key={metric.key} className={rowClass}>
        {index === 0 && (
          <td
            rowSpan={METRICS.length}
            className={`sticky left-0 z-10 border-r-2 border-gray-300 align-top ${firstCellClass}`}
          >
            {firstCell}
          </td>
        )}
        <td
          className={`whitespace-nowrap ${
            metric.key === "revenue" ? "font-semibold" : "text-gray-600"
          }`}
        >
          {metric.label}
        </td>
        {table.periods.map((period) => (
          <td
            key={period.key}
            className={`text-right whitespace-nowrap ${
              metric.key === "revenue" ? "font-semibold" : ""
            }`}
          >
            {formatCurrency(((cells[period.key] ?? ZERO_TRIPLE)[metric.key] as number))}
          </td>
        ))}
      </tr>
    ));
  };

  const renderSection = (section: ReportSection) => {
    const isExpandable = section.rows.length > 0;
    const isOpen = expanded[section.id] ?? false;

    const headerCell = isExpandable ? (
      <button
        type="button"
        onClick={() => toggleSection(section.id)}
        className="flex items-center gap-2 text-left font-medium"
        aria-expanded={isOpen}
      >
        <ChevronIcon
          className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
        {section.label}
        <span className="text-gray-500 font-normal">
          ({section.rows.length})
        </span>
      </button>
    ) : (
      <span className="font-medium">{section.label}</span>
    );

    return (
      <React.Fragment key={section.id}>
        {renderTripleRows(headerCell, section.subtotal, {
          rowClass: "bg-gray-50 border-t-2 border-gray-300",
          firstCellClass: "bg-gray-50",
        })}
        {isOpen &&
          section.rows.map((row) =>
            renderTripleRows(
              <span className="pl-6 block">{row.name}</span>,
              row.cells,
              { firstCellClass: "bg-white" }
            )
          )}
      </React.Fragment>
    );
  };

  return (
    <div className="min-w-0">
      <div className="flex items-start justify-between mb-6 gap-4">
        <div className="flex flex-col gap-2">
          <h2 className="font-medium">Revenue Sharing</h2>
          <div className="text-sm text-gray-600">
            RC revenue per source: total cashback, user cashback, and revenue
            (total − user) by month or quarter. Amounts in DKK.
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button
            variant="ghost"
            onClick={() => setAllExpanded(true)}
            disabled={!table}
            className="whitespace-nowrap text-sm"
          >
            Expand all
          </Button>
          <Button
            variant="ghost"
            onClick={() => setAllExpanded(false)}
            disabled={!table}
            className="whitespace-nowrap text-sm"
          >
            Collapse all
          </Button>
          <Button
            variant="outline"
            onClick={exportToCsv}
            disabled={!table}
            className="gap-2 whitespace-nowrap"
          >
            <DownloadIcon className="w-4 h-4" />
            Export CSV
          </Button>
        </div>
      </div>

      <div className="mb-6 flex items-end gap-4 flex-wrap">
        <div>
          <label htmlFor="rs-from" className="block text-sm text-gray-600 mb-1">
            From month
          </label>
          <select
            id="rs-from"
            value={fromMonth}
            onChange={(e) => setFromMonth(e.target.value)}
            className="p-2 border border-gray-300 rounded-md text-sm"
          >
            {monthOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="rs-to" className="block text-sm text-gray-600 mb-1">
            To month
          </label>
          <select
            id="rs-to"
            value={toMonth}
            onChange={(e) => setToMonth(e.target.value)}
            className="p-2 border border-gray-300 rounded-md text-sm"
          >
            {monthOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => applyQuickRange("ytd")}>
            YTD
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => applyQuickRange("last12Months")}
          >
            Last 12 months
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => applyQuickRange("last24Months")}
          >
            Last 24 months
          </Button>
        </div>
        <div>
          <label htmlFor="rs-granularity" className="block text-sm text-gray-600 mb-1">
            Group by
          </label>
          <select
            id="rs-granularity"
            value={granularity}
            onChange={(e) => setGranularity(e.target.value as Granularity)}
            className="p-2 border border-gray-300 rounded-md text-sm"
          >
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly</option>
          </select>
        </div>
        {loading && !initialLoad && <Loader />}
      </div>

      {fromMonth > toMonth && (
        <div className="mb-6 rounded-lg border border-red-300 bg-red-50 p-4 text-sm text-red-900">
          "From month" must not be after "To month".
        </div>
      )}

      {backendUnavailable && (
        <div className="mb-6 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
          <div className="font-medium mb-1">Awaiting backend deployment</div>
          <div>
            The endpoint{" "}
            <code>POST /business-account/get-revenue-sharing-report</code> is not
            available on this environment yet.
          </div>
        </div>
      )}

      {error && (
        <div className="mb-6 rounded-lg border border-red-300 bg-red-50 p-4 text-sm text-red-900 flex items-center justify-between gap-4">
          <span>Failed to load the revenue sharing report.</span>
          <Button variant="outline" size="sm" onClick={fetchReport}>
            Retry
          </Button>
        </div>
      )}

      {table && (
        <>
          <div className="overflow-x-auto">
            <table className="table-default">
              <thead>
                <tr>
                  <th className="sticky left-0 bg-gray-50 z-20 min-w-[220px] border-r-2 border-gray-300">
                    Section / Partner
                  </th>
                  <th className="min-w-[130px]">Metric</th>
                  {table.periods.map((period) => (
                    <th
                      key={period.key}
                      className="min-w-[120px] text-right whitespace-nowrap border-l border-gray-300"
                    >
                      {periodHeader(period)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {table.sections.map(renderSection)}
                {renderTripleRows(
                  <span className="font-semibold">Grand total</span>,
                  table.grandTotal,
                  {
                    rowClass: "bg-blue-50 border-t-2 border-gray-400",
                    firstCellClass: "bg-blue-50",
                  }
                )}
              </tbody>
            </table>
          </div>
          {hasPartialQuarter && (
            <div className="mt-2 text-xs text-gray-500">
              * Partial quarter — only part of it falls inside the selected
              range.
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminRevenueSharing;
